import React from 'react';
import { Shield, Crown, Star, Users, User } from 'lucide-react';
import Icon from '../AppIcon';


/**
 * RoleBadge Component
 * 
 * Displays a color-coded role badge with hierarchy indicator and tooltip
 * 
 * @param {Object} props
 * @param {string} props.roleName - Internal role name (e.g., 'super_admin')
 * @param {string} props.displayName - User-friendly role name (e.g., 'Super Administrator')
 * @param {number} props.hierarchyLevel - Role hierarchy level (100=highest, 20=lowest)
 * @param {boolean} props.showHierarchy - Show hierarchy level indicator
 * @param {boolean} props.showIcon - Show role icon
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.variant - Style variant: 'badge', 'pill', 'minimal'
 */
export default function RoleBadge({
  roleName,
  displayName,
  hierarchyLevel = 50,
  showHierarchy = true,
  showIcon = true,
  size = 'md',
  variant = 'badge'
}) {
  // Role color mapping based on hierarchy and name
  const getRoleColor = () => {
    const colorMap = {
      'super_admin': {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-200',
        icon: 'text-purple-600'
      },
      'business_admin': {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-200',
        icon: 'text-blue-600'
      },
      'manager': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        icon: 'text-green-600'
      },
      'staff': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: 'text-gray-600'
      },
      'customer': {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-200',
        icon: 'text-orange-600'
      }
    };

    return colorMap?.[roleName] || colorMap?.['staff'];
  };

  // Get appropriate icon based on hierarchy
  const getRoleIcon = () => {
    if (hierarchyLevel >= 90) return Crown;
    if (hierarchyLevel >= 70) return Shield;
    if (hierarchyLevel >= 50) return Star;
    if (hierarchyLevel >= 30) return Users;
    return User;
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };

  // Variant classes
  const variantClasses = {
    badge: 'rounded-md border',
    pill: 'rounded-full',
    minimal: 'rounded border-0'
  };

  const colors = getRoleColor();
  const Icon = getRoleIcon();

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 font-medium
        ${colors?.bg} ${colors?.text} ${colors?.border}
        ${sizeClasses?.[size]}
        ${variantClasses?.[variant]}
        transition-all duration-200 hover:shadow-sm
      `}
      title={`${displayName} (Level ${hierarchyLevel})`}
    >
      {showIcon && (
        <Icon className={`w-3.5 h-3.5 ${colors?.icon}`} />
      )}
      <span className="font-semibold">{displayName}</span>
      {showHierarchy && (
        <span className="text-xs opacity-75 ml-1">
          L{hierarchyLevel}
        </span>
      )}
    </div>
  );
}